import type {
  RawExtensions,
  RawEnrichedAttributes,
  RawVariationAttributeMappings,
  RawDefaultVariation,
  RawEnrichedCategories,
  RawEnrichedTagsData,
  RawProductTranslations,
  RawVariationGallery,
  RawProductAdditionalInfo,
  RawDeliveryEstimationExtension,
  RawReturnAvailabilityExtension,
  RawBuyXPayYExtension,
  RawFirstOrderDiscountExtension, // New Import
} from "../../../../../../types";

/**
 * The shape of the extensions object after normalization.
 * All properties are guaranteed to exist and have a default shape.
 */
interface NormalizedExtensions {
  relatedProducts: { upsell_ids: number[]; cross_sell_ids: number[] };
  enrichedAttributes: RawEnrichedAttributes;
  variationAttributeMappingsData: RawVariationAttributeMappings;
  defaultVariation: RawDefaultVariation | null;
  enrichedCategories: RawEnrichedCategories;
  enrichedTags: RawEnrichedTagsData;
  translations: RawProductTranslations | Record<string, never>;
  variationGalleryData: RawVariationGallery;
  additionalInfo: RawProductAdditionalInfo;
  deliveryEstimation: RawDeliveryEstimationExtension | undefined;
  returnAvailability: RawReturnAvailabilityExtension | undefined;
  buyXPayY: RawBuyXPayYExtension | undefined;
  firstOrderDiscount: RawFirstOrderDiscountExtension | undefined; // Added First Order Discount
}

export const normalizeProductExtensions = (
  extensions: RawExtensions | undefined,
): NormalizedExtensions => {
  // Use a type assertion to tell TypeScript to treat the object as RawExtensions.
  // The subsequent logic handles cases where properties are missing.
  const safeExtensions = (extensions || {}) as RawExtensions;

  return {
    relatedProducts:
      !Array.isArray(safeExtensions.related_products) &&
      safeExtensions.related_products
        ? safeExtensions.related_products
        : { upsell_ids: [], cross_sell_ids: [] },
    enrichedAttributes: safeExtensions.enriched_attributes || {
      attributes: [],
    },
    variationAttributeMappingsData:
      !Array.isArray(safeExtensions.variation_attribute_mappings) &&
      safeExtensions.variation_attribute_mappings
        ? safeExtensions.variation_attribute_mappings
        : { items: [] },
    defaultVariation:
      !Array.isArray(safeExtensions.default_variation) &&
      safeExtensions.default_variation
        ? safeExtensions.default_variation
        : null,
    enrichedCategories: safeExtensions.enriched_categories || { items: [] },
    enrichedTags: safeExtensions.enriched_tags || { items: [] },
    translations:
      !Array.isArray(safeExtensions.product_translations) &&
      safeExtensions.product_translations
        ? safeExtensions.product_translations
        : {},
    variationGalleryData: safeExtensions.variation_gallery || { items: [] },
    additionalInfo: safeExtensions.product_additional_info || {},
    deliveryEstimation: safeExtensions.delivery_estimation,
    returnAvailability: safeExtensions.return_availability,
    buyXPayY: safeExtensions.buy_x_pay_y_promotion,
    firstOrderDiscount: safeExtensions.first_order_discount, // Mapped First Order Discount
  };
};
